import React, { forwardRef, Ref, useCallback, useState } from 'react';
import { Collapsible } from './Collapsible';
import { Tool } from './Tool';
import { LoadMask } from './LoadMask';
import {
  ChildrenProp,
  ClassNameProp,
  ClosableComponentProps,
  CollapsibleComponentProps,
  Direction,
  DirectionProp,
  MaskableComponentProps,
  ToolableComponentProps
} from '../common-types';
import { ValidIconName } from '..';
import { intl } from '../../lib';

/**
 * Properties for a {@link Panel} component.
 * @author agd
 */
export interface BasePanelProps
  extends ClosableComponentProps,
    CollapsibleComponentProps,
    ToolableComponentProps,
    DirectionProp,
    Required<ChildrenProp>,
    ClassNameProp,
    MaskableComponentProps {
  /**
   * Panel title.
   */
  title?: string | JSX.Element;
  /**
   * Panel actions.
   */
  actions?: React.ReactNode;

  /**
   * Indicates if this panel should become a main panel.
   */
  mainPanel?: boolean;

  /**
   * The heading level of the Panel's title.
   */
  titleHeadingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  /**
   * Makes panel content section tabbable with tabindex=0.
   */
  tabbableContent?: true;
}

/**
 * Properties for a {@link Panel} component with collapsible=true.
 * @author boris.brtan
 */
export interface CollapsiblePanelProps extends BasePanelProps {
  /**
   * Component is collapsible.
   */
  collapsible: true;
  /**
   * Aria-label to set for the Collapsible.
   */
  'aria-label': string;
}

/**
 * Properties for a {@link Panel} component with collapsible=false|undefined.
 * @author boris.brtan
 */
export interface NonCollapsiblePanelProps extends BasePanelProps {
  /**
   * Component is not collapsible.
   */
  collapsible?: false;
  /**
   * Aria-label should not be defined if collapsible=false|undefined
   */
  'aria-label'?: undefined;
}

/**
 * Props for a regular panel.
 * Union type for CollapsiblePanelProps and NonCollapsiblePanelProps properties.
 */
export type PanelProps = CollapsiblePanelProps | NonCollapsiblePanelProps;

/**
 * Props for a panel, which uses a <form> HTML element.
 * @author agd
 */
export type FormPanelProps = PanelProps &
  Partial<
    Omit<
      React.DetailedHTMLProps<
        React.FormHTMLAttributes<HTMLFormElement>,
        HTMLFormElement
      >,
      'ref'
    >
  >;

/**
 * Helper type to determine if PanelProps or FormPanelProps are expected, depending on the variant.
 * @author agd
 */
type PanelOrFormPanel<
  DomElement extends 'form' | 'div'
> = DomElement extends 'form' ? FormPanelProps : PanelProps;

/**
 * Panel component implementation.
 *
 * @param props the {@link PanelProps}
 * @return the created {@link JSX.Element}.
 * @constructor
 * @author agd
 */
const PanelComponent = forwardRef(function PanelComponent<
  DomElement extends 'form' | 'div'
>(
  {
    title,
    collapsible = false,
    openInitially = true,
    closable = false,
    children,
    tools,
    actions,
    className,
    direction,
    masked = false,
    mainPanel = false,
    collapsed,
    setCollapsed,
    domElementType,
    tabbableContent,
    titleHeadingLevel: HeadingTag = 'h2',
    ...props
  }: PanelOrFormPanel<DomElement> & { domElementType: DomElement },
  ref: Ref<any>
): JSX.Element {
  // callback to determine which classes to set when the component is collapsed
  // these classes will be returned as an array
  const determineCollapsedClasses = useCallback(() => {
    return direction ? ['collapsed', `collapsed-${direction}`] : ['collapsed'];
  }, [direction]);

  // css classes that will be added to the panel if it's collapsed
  const [collapsedClasses, setCollapsedClasses] = useState<Array<string>>(
    openInitially ? [] : determineCollapsedClasses()
  );
  const closeTool = closable && (
    <Tool
      icon="shui-cancel"
      onClick={closable.onClose}
      tooltip={intl('view.button.close')}
    />
  );
  // the closeTool should always be the last tool
  const toolsElement = (
    <>
      {tools}
      {closeTool}
    </>
  );
  // content inside the panel
  const panelContentElement = (
    <div className="panel-content" tabIndex={tabbableContent && 0}>
      {children}
    </div>
  );
  // panel actions
  const actionsElement = actions ? (
    <div className="actions">{actions}</div>
  ) : (
    <></>
  );

  // handle collapsible state
  const onExpand = useCallback((): void => {
    setCollapsedClasses([]);
  }, []);

  const onCollapse = useCallback((): void => {
    setCollapsedClasses(determineCollapsedClasses());
  }, [determineCollapsedClasses]);

  // when the collapsed state is controlled
  const collapsedControlled = collapsed !== undefined && setCollapsed;
  const getControlledCollapsedClasses = useCallback((): string[] => {
    let controlledCollapsedClasses: string[] = [];
    if (collapsedControlled && collapsed) {
      controlledCollapsedClasses = determineCollapsedClasses();
    }
    return controlledCollapsedClasses;
  }, [collapsed, collapsedControlled, determineCollapsedClasses]);

  // get collapsible icons
  let collapsibleOpenIcon: ValidIconName | undefined = undefined,
    collapsibleClosedIcon: ValidIconName | undefined = undefined;
  if (direction && direction === Direction.LEFT) {
    collapsibleOpenIcon = 'materialicon-keyboard_arrow_left';
    collapsibleClosedIcon = 'materialicon-keyboard_arrow_right';
  } else if (direction && direction === Direction.RIGHT) {
    collapsibleOpenIcon = 'materialicon-keyboard_arrow_right';
    collapsibleClosedIcon = 'materialicon-keyboard_arrow_left';
  } // else just use the defaults

  // the panel classes
  const panelClasses = ['ehs-panel'];
  // determine if it is a main panel, if so add  only a certain class.
  if (mainPanel) panelClasses.push('main-panel');
  if (collapsedControlled) {
    panelClasses.push(...getControlledCollapsedClasses());
  } else {
    panelClasses.push(...collapsedClasses);
  }
  if (className) panelClasses.push(className);

  const innerElement = (
    <>
      {collapsible && title ? (
        <Collapsible
          triggerText={title}
          triggerSibling={toolsElement}
          openInitially={openInitially}
          onCollapse={onCollapse}
          onExpand={onExpand}
          openIcon={collapsibleOpenIcon}
          collapsedIcon={collapsibleClosedIcon}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          aria-label={props['aria-label'] as string}
        >
          {panelContentElement}
          {actionsElement}
        </Collapsible>
      ) : (
        <>
          {(title || tools) && (
            <div className="panelTitle">
              {title && (
                <HeadingTag className="panelTitle-text">{title}</HeadingTag>
              )}
              <div className="tools">{toolsElement}</div>
            </div>
          )}
          {panelContentElement}
          {actionsElement}
        </>
      )}
      <LoadMask masked={masked} />
    </>
  );
  return domElementType === 'form' ? (
    <form {...props} className={panelClasses.join(' ')} ref={ref}>
      {innerElement}
    </form>
  ) : (
    <div className={panelClasses.join(' ')} ref={ref}>
      {innerElement}
    </div>
  );
});

/**
 * A simple panel component.
 *
 * @param props the {@link PanelProps}
 * @return the created {@link JSX.Element}.
 * @constructor
 * @author agd
 */
export const Panel = forwardRef(function Panel(
  props: PanelProps,
  ref: Ref<HTMLDivElement>
): JSX.Element {
  return <PanelComponent domElementType="div" {...props} ref={ref} />;
});

/**
 * A panel component, which uses a <form> element.
 * This is for use-cases where things like a managedForm's submit button go into the `actions`, etc.
 *
 * @param props the {@link FormPanelProps}
 * @return the created {@link JSX.Element}.
 * @constructor
 * @author agd
 */
export const FormPanel = forwardRef(function FormPanel(
  props: FormPanelProps,
  ref: Ref<HTMLFormElement>
): JSX.Element {
  return <PanelComponent domElementType="form" {...props} ref={ref} />;
});
